import { IconButton as MuiIconButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type { AriaAttributes, ButtonHTMLAttributes, ReactNode } from "react";

const CUSTOM_COLORS = ["interactive", "accent-bordered"] as const;
type CustomColor = (typeof CUSTOM_COLORS)[number];

function isCustomColor(color: string | undefined): color is CustomColor {
  return (
    color !== undefined && (CUSTOM_COLORS as readonly string[]).includes(color)
  );
}

export type IconButtonProps = AriaAttributes &
  Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "onClick" | "disabled" | "type" | "id"
  > & {
    children: ReactNode;
    size?: "small" | "medium" | "large";
    color?:
      | "inherit"
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "warning"
      | "info"
      | "success"
      // TODO: name is provisional — "interactive" matches the token but is semantically vague.
      // Candidates: "accent", "brand", "subdued". Raise with team before adding more token-colors.
      | "interactive"
      // TODO: name is provisional — encodes accent token + border + disabled state for CounterButton.
      // Same naming concern as "interactive"; raise with team together.
      | "accent-bordered";
    // Suppresses MUI's default padding via sx internally; Tailwind `p-*` classes lose
    // to unlayered emotion styles so this must live inside the abstraction.
    disablePadding?: boolean;
    loading?: boolean;
  };

const customColorSx: Record<CustomColor, SxProps<Theme>> = {
  interactive: {
    color: "var(--color-text-interactive)",
    "&.Mui-disabled": { color: "var(--color-text-disabled-interactive)" },
  },
  "accent-bordered": {
    border: "1px solid",
    borderColor: "var(--color-surface-accent-strong)",
    color: "var(--color-surface-accent-strong)",
    transition: "border-color 0.2s, color 0.2s",
    "&.Mui-disabled": {
      borderColor: "var(--color-surface-disabled-accent-strong)",
      color: "var(--color-surface-disabled-accent-strong)",
    },
  },
};

export const IconButton = ({
  children,
  disablePadding,
  color,
  ...props
}: IconButtonProps) => {
  const sx: SxProps<Theme> = {
    ...(disablePadding ? { p: 0 } : undefined),
    ...(isCustomColor(color) ? customColorSx[color] : undefined),
  };

  return (
    <MuiIconButton
      color={isCustomColor(color) ? undefined : color}
      sx={Object.keys(sx).length > 0 ? sx : undefined}
      {...props}
    >
      {children}
    </MuiIconButton>
  );
};
